import { useEffect } from 'react';
import {
  useMatch,
  useNavigate,
  useParams,
  generatePath,
} from 'react-router-dom';
import { PermissionEnum } from '@ui/src';
import { useAppSelector } from '@store';
import OverviewTopContent from './parts/overview-top-content';
import OverviewPanels from './parts/overview-panels';
import OverviewCompanies from './parts/companies';
import OverviewStatements from '../../common/statements';
import GeneralUserInfo from '../../../components/individual/overview-general-info/general-user-info';
import CustomerComments from '../../../components/common/overview-comments/comments';
import SummaryInfo from '../../../components/common/overview-summary/info';
import TopUpOptions from '../../common/top-up-options';
import { OrganizationType } from '../../common/types';
import useOverview from './hooks/use-overview';

import ROUTES from '../../../routes';

import commonStyles from '../../common/common.module.css';

const IndividualCustomerOverview = (): React.JSX.Element => {
  const {
    baseInformation,
    baseInformationIsLoading,
    totalBalance,
    balances,
    customerComments,
    customerCommentsPagination,
  } = useAppSelector((state) => state.individualCustomers.info.general);
  const { baseUserInformation } = useAppSelector(
    (state) => state.individualCustomers.info.overview,
  );
  const {
    cardsEnabled,
    sourcesOfFunds,
    clientsActivities,
    productsIndividual,
  } = useAppSelector((state) => state.customersDictionaries);
  const { users } = useAppSelector((state) => state.administrationDictionaries);
  const {
    activePermissions,
    currenciesList,
    currencies: dictionariesCurrencies,
    baseCurrency,
    countriesList,
  } = useAppSelector((state) => state.root);

  const { customerId } = useParams();
  const match = useMatch(ROUTES.CUSTOMERS_INDIVIDUAL_OVERVIEW);
  const navigate = useNavigate();

  const {
    control,
    onInfoSubmit,
    isLoading,
    isSuccess,
    onResetPassword,
    recoveryInProgress,
    globalError,
    errors,
    commentsIsLoading,
    commentsIsFetching,
    getCustomerCommentsAction,
    setConvertPopuVisible,
    setTopUpPanelIsOpen,
    setSelectedTopUpId,
    onAddCommentToCustomer,
    addCommentInProgress,
    addCommentIsSuccess,
    onEditCommentInCustomer,
    editCommentInProgress,
    editCommentIsSuccess,
    onDeleteCommentInCustomer,
    deleteCommentInProgress,
    deleteCommentIsSuccess,
    topUpPanelIsOpen,
    selectedTopUpId,
    convertIsLoading,
    convertPopupVisible,
    convertToIndividaul,
    reopenPopupState,
    setReopenPopupVisible,
    setReopenPopupState,
    setReopenError,
    reopenInProgress,
    reopenPopupVisible,
    reopenReason,
    onReopenOrganization,
    reopenError,
    setReopenReason,
    setResetPasswordPopupVisible,
    resetPasswordPopupVisible,
    link,
    token,
    copyIsSuccess,
    onCopy,
    changeLogsPanelIsOpen,
    setChangeLogPanelIsOpen,
    changeLogsIsLoading,
    changeLogsIsFetching,
    getChangeLogsAction,
  } = useOverview({
    customerId,
    baseInformation,
    activePermissions,
    baseUserInformation,
  });

  useEffect(() => {
    if (customerId && match) {
      navigate(
        generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_OVERVIEW_TRANSACTIONS, {
          customerId,
        }),
      );
    }
  }, [customerId, match]);

  useEffect(() => {
    let title = baseUserInformation?.fullName
      ? `${baseUserInformation?.fullName}: Overview`
      : 'Overview';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, [baseUserInformation?.fullName]);

  return (
    <div className={commonStyles.customerContent}>
      <OverviewTopContent
        setChangeLogPanelIsOpen={setChangeLogPanelIsOpen}
        setReopenPopupVisible={setReopenPopupVisible}
      />
      <SummaryInfo
        accounts={baseInformation?.accounts?.length as number}
        currencies={totalBalance?.currency as string[]}
        balances={balances}
        balance={totalBalance?.balance as number}
        type={baseUserInformation?.type}
        cardsEnabled={cardsEnabled}
        currenciesList={currenciesList}
        dictionariesCurrencies={dictionariesCurrencies}
        baseCurrency={baseCurrency}
      />
      <GeneralUserInfo
        isLoading={baseInformationIsLoading as boolean}
        generalUserInfo={baseInformation}
        baseUserInformation={baseUserInformation}
        control={control}
        onInfoSubmit={onInfoSubmit}
        requestInProgress={isLoading}
        isSuccess={isSuccess}
        onResetPassword={onResetPassword}
        recoveryInProgress={recoveryInProgress}
        globalError={globalError}
        errors={errors}
        setConvertPopuVisible={setConvertPopuVisible}
        convertToIndividualAvailable={Boolean(
          activePermissions?.includes(
            PermissionEnum.COFF_CUSTOMERS_CONVERT_TO_INDIVIDUAL_ACTION,
          ),
        )}
        activePermissions={activePermissions}
        status={baseInformation?.status}
        sourcesOfFunds={sourcesOfFunds}
        clientsActivities={clientsActivities}
        countries={countriesList}
        productsIndividual={productsIndividual}
      />
      {activePermissions?.includes(
        PermissionEnum.INDIVIDUAL_CUSTOMERS_COMMENTS_LIST,
      ) && (
        <CustomerComments
          comments={customerComments}
          pagination={customerCommentsPagination}
          isLoading={commentsIsLoading}
          isFetching={commentsIsFetching}
          getCommentsList={getCustomerCommentsAction}
          createAvailable={activePermissions?.includes(
            PermissionEnum.INDIVIDUAL_CUSTOMERS_COMMENTS_CREATE_ACTION,
          )}
          editAvailable={activePermissions?.includes(
            PermissionEnum.INDIVIDUAL_CUSTOMERS_COMMENTS_EDIT_ACTION,
          )}
          deleteAvailable={activePermissions?.includes(
            PermissionEnum.INDIVIDUAL_CUSTOMERS_COMMENTS_DELETE_ACTION,
          )}
          onAddCommentToCustomer={onAddCommentToCustomer}
          addCommentInProgress={addCommentInProgress}
          addCommentIsSuccess={addCommentIsSuccess}
          onEditCommentInCustomer={onEditCommentInCustomer}
          editCommentInProgress={editCommentInProgress}
          editCommentIsSuccess={editCommentIsSuccess}
          users={users}
          onDeleteCommentInCustomer={onDeleteCommentInCustomer}
          deleteCommentInProgress={deleteCommentInProgress}
          deleteCommentIsSuccess={deleteCommentIsSuccess}
          changeLogsAvailable={activePermissions?.includes(
            PermissionEnum.AUDIT_LOG_LIST,
          )}
        />
      )}
      <OverviewCompanies
        isLoading={baseInformationIsLoading}
        status={baseInformation?.status}
      />
      {baseUserInformation?.type !== OrganizationType.COMPANY_OFFICIAL && (
        <TopUpOptions
          customerType={OrganizationType.INDIVIDUAL}
          organizationId={customerId}
          setTopUpPanelIsOpen={setTopUpPanelIsOpen}
          setSelectedTopUpId={setSelectedTopUpId}
          status={baseInformation?.status}
        />
      )}
      {activePermissions?.includes(
        PermissionEnum.CUSTOMERS_STATEMENTS_LIST,
      ) && (
        <OverviewStatements
          matchPath={ROUTES.CUSTOMERS_INDIVIDUAL_OVERVIEW_TRANSACTIONS}
          cardsRoute={ROUTES.CUSTOMERS_INDIVIDUAL_CARDS}
          routes={{
            transationRoute: ROUTES.CUSTOMERS_INDIVIDUAL_OVERVIEW_TRANSACTIONS,
            cardsRoute: ROUTES.CUSTOMERS_INDIVIDUAL_OVERVIEW_CARDS,
          }}
          accounts={
            baseInformation?.id === customerId
              ? baseInformation?.accounts
              : undefined
          }
          cards={baseInformation?.cards as string[]}
          cardsEnabled={cardsEnabled}
          balances={balances}
          type={baseUserInformation?.type}
        />
      )}
      <OverviewPanels
        topUpPanelIsOpen={topUpPanelIsOpen}
        setTopUpPanelIsOpen={setTopUpPanelIsOpen}
        selectedTopUpId={selectedTopUpId}
        setSelectedTopUpId={setSelectedTopUpId}
        setConvertPopuVisible={setConvertPopuVisible}
        convertIsLoading={convertIsLoading}
        convertPopupVisible={convertPopupVisible}
        convertToIndividaul={convertToIndividaul}
        reopenPopupState={reopenPopupState}
        setReopenPopupVisible={setReopenPopupVisible}
        setReopenPopupState={setReopenPopupState}
        setReopenError={setReopenError}
        reopenInProgress={reopenInProgress}
        reopenPopupVisible={reopenPopupVisible}
        reopenReason={reopenReason}
        onReopenOrganization={onReopenOrganization}
        reopenError={reopenError}
        setReopenReason={setReopenReason}
        setResetPasswordPopupVisible={setResetPasswordPopupVisible}
        resetPasswordPopupVisible={resetPasswordPopupVisible}
        link={link}
        token={token}
        copyIsSuccess={copyIsSuccess}
        onCopy={onCopy}
        changeLogsPanelIsOpen={changeLogsPanelIsOpen}
        setChangeLogPanelIsOpen={setChangeLogPanelIsOpen}
        changeLogsIsLoading={changeLogsIsLoading}
        changeLogsIsFetching={changeLogsIsFetching}
        getChangeLogsAction={getChangeLogsAction}
      />
    </div>
  );
};

export default IndividualCustomerOverview;
